import { Database } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { SourceStatus } from '../api';

/** How each document state reads on the badge. */
const LABELS: Record<NonNullable<SourceStatus['status']>, string> = {
  pending: 'Syncing…',
  indexed: 'Synced',
  stale: 'Sync stale',
  failed: 'Sync failed',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Compact indicator that a source (wiki/project page, attachment) is in the
 * Knowledge Base. Renders nothing until the entity has actually been synced —
 * safe to drop next to a title or in a list row with an undefined status.
 */
export function KbSyncBadge({
  status,
  className,
}: {
  status: SourceStatus | undefined;
  className?: string;
}) {
  if (!status?.synced) return null;

  const state = status.status ?? 'indexed';
  const when = fmtDate(status.last_synced_at);
  const tone =
    state === 'failed'
      ? 'text-destructive border-destructive/40'
      : state === 'stale'
        ? 'text-amber-600 border-amber-500/40'
        : 'text-emerald-700 border-emerald-600/40 dark:text-emerald-400';

  return (
    <Badge
      variant="outline"
      title={
        when
          ? `In the Knowledge Base · last synced ${when}`
          : 'In the Knowledge Base'
      }
      className={cn('gap-1 font-normal', tone, className)}
    >
      <Database className="size-3" />
      {LABELS[state]}
    </Badge>
  );
}
